import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, Mail, MessageSquare, Copy, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const MarketingTemplates = () => {
  const { token, getAuthHeaders, isCalendarAdmin } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    channel: 'email'
  });

  // Available variables
  const variables = [
    { name: '&customer-name', description: "Customer's full name" },
    { name: '&customer-email', description: "Customer's email address" },
    { name: '&date', description: 'Current date' }
  ];

  useEffect(() => {
    if (!isCalendarAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchTemplates();
  }, [isCalendarAdmin, navigate]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/marketing/templates`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', body: '', channel: 'email' });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      channel: template.channel || 'email'
    });
    setShowModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/marketing/templates/${templateId}`, {
        headers: getAuthHeaders()
      });
      setSuccess('Template deleted successfully');
      fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      if (editingTemplate) {
        await axios.put(
          `${API_BASE_URL}/marketing/templates/${editingTemplate.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        setSuccess('Template updated successfully');
      } else {
        await axios.post(
          `${API_BASE_URL}/marketing/templates`,
          formData,
          { headers: getAuthHeaders() }
        );
        setSuccess('Template created successfully');
      }
      
      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (varName) => {
    setFormData(prev => ({
      ...prev,
      body: prev.body + varName
    }));
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(varName);
    setSuccess(`Copied ${varName} to clipboard`);
    setTimeout(() => setSuccess(''), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate('/marketing')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketing
          </button>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Marketing Templates</h1>
              <p className="text-gray-600 mt-2">Create and manage reusable message templates</p>
            </div>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
              data-testid="create-template-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Template
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : templates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Templates Yet</h3>
              <p className="text-gray-500 mb-4">Create your first marketing template to get started</p>
              <button
                onClick={handleCreate}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
              >
                Create Template
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map(template => (
                <div key={template.id} className="bg-white rounded-lg shadow p-6" data-testid={`template-${template.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {template.channel === 'sms' ? (
                          <MessageSquare className="w-5 h-5 text-blue-500" />
                        ) : (
                          <Mail className="w-5 h-5 text-yellow-500" />
                        )}
                        <h3 className="text-lg font-semibold text-gray-800">{template.name}</h3>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded uppercase">
                          {template.channel}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Subject:</strong> {template.subject}
                      </p>
                      <p className="text-sm text-gray-500 line-clamp-2">{template.body}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        data-testid={`edit-${template.id}`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        data-testid={`delete-${template.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Holiday Promotion"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="email"
                      checked={formData.channel === 'email'}
                      onChange={e => setFormData({ ...formData, channel: e.target.value })}
                      className="w-4 h-4 text-yellow-500"
                    />
                    <span className="ml-2 text-sm">Email</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="sms"
                      checked={formData.channel === 'sms'}
                      onChange={e => setFormData({ ...formData, channel: e.target.value })}
                      className="w-4 h-4 text-yellow-500"
                    />
                    <span className="ml-2 text-sm">SMS</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Special Offer Just for You!"
                  data-testid="template-subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message Body *</label>
                <textarea
                  value={formData.body}
                  onChange={e => setFormData({ ...formData, body: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono text-sm"
                  rows={6}
                  placeholder="Hi &customer-name, we have an exciting offer for you..."
                  data-testid="template-body-input"
                />
              </div>

              {/* Variables panel */}
              <div className="bg-gray-50 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Available Variables</h4>
                <div className="flex flex-wrap gap-2">
                  {variables.map(v => (
                    <div key={v.name} className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => insertVariable(v.name)}
                        className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono hover:bg-gray-100 transition-colors"
                        title={`Insert ${v.name}`}
                      >
                        {v.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyVariable(v.name)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-semibold rounded-md transition-colors"
                data-testid="save-template-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MarketingTemplates;
